import isNil from 'lodash/isNil';
import { ICachedCohortEntity } from '../../redux/slices/cachedEntities';
import {
  ExpressionCohortOperatorKey,
  FunnelCohortOperatorKey,
  ITimeToConvertInput,
  RetentionCohortOperatorKey,
  SimpleCohortTypeKey,
} from '../apollo/graphqlSchema';
import { parseRangeBucket } from './parseRangeBucket';
import { IAnalysisQueryResponse } from '../apollo/responseTypes';
import {
  ICohortDurationStart,
  ILookForwardWindow,
} from '../../redux/slices/oldBuilder';
import { MaybeNil } from '../interfaces';
import { assertCondition } from '../types';

interface IProps {
  analysisId: string;
  measureId?: string;
  // if true, we cannot reset cached analyses in Cohort page
  // since the status of new analysis will keep PENDING by ANALYSIS_PREPARE_COHORT
  referToNewMeasure?: boolean;
  type: SimpleCohortTypeKey;
  startDate: string;
  endDate: string;
  builderInfo: IAnalysisQueryResponse;
  isExecuteAfterRedirect: boolean;
  histogramBucket?: MaybeNil<string>;
  index?: number;
  isChurn?: boolean;
  cohortDurationStart?: ICohortDurationStart;
  lookForwardWindow?: ILookForwardWindow;
  timeToConvertInput?: MaybeNil<ITimeToConvertInput>;
}

export default ({
  analysisId,
  measureId,
  referToNewMeasure,
  type,
  startDate,
  endDate,
  builderInfo,
  histogramBucket,
  index,
  isChurn,
  isExecuteAfterRedirect,
  cohortDurationStart,
  lookForwardWindow,
  timeToConvertInput,
}: IProps): ICachedCohortEntity => {
  const cachedCohortData: ICachedCohortEntity = {
    analysisId,
    type,
    startDate,
    endDate,
    subjectKey:
      'subject' in builderInfo ? builderInfo?.subject?.key : undefined,
    schemaId: builderInfo.schema.id,
    operator: '',
    operatorValues: [null, null],
    isExecuteAfterRedirect,
    cohortDurationStart,
    lookForwardWindow,
    timeToConvertInput: timeToConvertInput ?? undefined,
  };

  if (type === SimpleCohortTypeKey.Expression) {
    assertCondition(builderInfo.__typename === 'ExpressionQuery');
    cachedCohortData.measureId = measureId;
    cachedCohortData.referToNewMeasure = referToNewMeasure;
  }
  // Cohort creation from Retention
  if (type === SimpleCohortTypeKey.Retention) {
    cachedCohortData.operator = isChurn
      ? RetentionCohortOperatorKey.NotRetainedSince
      : RetentionCohortOperatorKey.RetainedOn;

    // index is amount of days
    if (!isNil(index)) {
      cachedCohortData.operatorValues[0] = index;
    }

    // there is no extra logic for Retention, so that's why escape early
    return cachedCohortData;
  }

  // Cohort creation from Funnel
  if (type === SimpleCohortTypeKey.Funnel) {
    cachedCohortData.operator = FunnelCohortOperatorKey.ConvertedOn;

    // index is a step index
    if (!isNil(index)) {
      cachedCohortData.operatorValues = [index, null];
    }

    return cachedCohortData;
  }

  // Cohort creation from Query
  if (type === SimpleCohortTypeKey.Expression) {
    cachedCohortData.operator = ExpressionCohortOperatorKey.Greaterthan;
    cachedCohortData.operatorValues = [0, null];

    if (!isNil(histogramBucket)) {
      const parsedRangeBucket = parseRangeBucket(histogramBucket);

      if (parsedRangeBucket) {
        const { operator, operatorValues } = parsedRangeBucket;

        cachedCohortData.operator = operator;
        cachedCohortData.operatorValues = operatorValues;
      }

      return cachedCohortData;
    }
  }
  return cachedCohortData;
};
